import { IIntakeModel, IntakeModel, IntakePetModel } from "@common/models";
import { IntakeService, ZipCodeService } from '@common/services';
import { NotificationStoreInstance } from "@common/stores";
import { find } from "lodash";
import { DateTime } from "luxon";
import { action, computed, flow, makeObservable, observable } from "mobx";

export class IntakeStore {
    @observable intakePublicId: string = '';
    @observable linkSource: string = 'email';
    @observable intake: IntakeModel = new IntakeModel();
    @observable isReadOnly: boolean = false;
    @observable loading: boolean = true;
    @observable selectedIntakePetId: number = 0;
    @observable selectedIntakePetIndex: number = -1;

    constructor() {
        makeObservable<IntakeStore>(this, undefined, {
            autoBind: true
        });
    }

    @computed get selectedPet(): IntakePetModel | undefined {
        if (this.selectedIntakePetId == 0) return undefined;
        return find(this.intake.pets, (pet) => {
            return pet.intakePetId == this.selectedIntakePetId;
        });
    }

    //#region Actions

    @action.bound setParamValues(intakePublidId: string, linkSource?: string, intakepetid?: number) {
        this.intakePublicId = intakePublidId;
        if (linkSource)
            this.linkSource = linkSource;

        if (intakepetid)
            this.selectedIntakePetId = intakepetid;
    }

    @action.bound intakeIsReadOnly(): boolean {
        const today = DateTime.now();

        if (!this.intake.expectedFullVisitDate)
            return true;

        const visitDate = DateTime.fromJSDate(this.intake.expectedFullVisitDate);
        const visitDateAlreadyHappened = today.startOf("day") > visitDate;

        if (visitDate && visitDateAlreadyHappened && this.linkSource === 'petpass')
            return true;

        return false;
    }

    //#endregion

    //#region Flows

    getZipCode = flow(function* (this: IntakeStore, zipCode: string) {
        if (zipCode) {
            yield ZipCodeService.getZipCodeByZipcode(zipCode + '?vpca=351BD313-2C19-442B-BC87-C69356EB59EF')
                .then((zipCode) => {
                    if (zipCode && zipCode.zipCodeId !== 0) {
                        this.intake.city = zipCode.city;
                        this.intake.county = zipCode.county;
                        this.intake.stateId = zipCode.stateId;
                        this.intake.stateAbbrev = zipCode.state?.abbreviation;
                        this.intake.zipCode = zipCode.zipcode;
                    }
                    else {
                        NotificationStoreInstance.error("Please enter a valid Zip Code.");
                    }
                })
                .catch(() => {
                    NotificationStoreInstance.error("Unfortunately we can not look up zip codes at this time.");
                });
        }

    });

    getIntake = flow(function* (this: IntakeStore) {
        this.intake = yield IntakeService.getIntake(this.intakePublicId, { showError: true });

        this.isReadOnly = this.intake.isReadOnly || this.intake.isClinicCancelled || this.intakeIsReadOnly()

        // Track the opening of the intake
        if (this.intake) {
            yield IntakeService.openIntake(this.intake.intakeId, this.linkSource, { showError: true });
        }
    });

    updateIntake = flow(function* (this: IntakeStore, intake: IIntakeModel) {
        return yield IntakeService.updateIntake(intake, this.linkSource, { showError: true });
    });

    //#endregion
}
